import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { callClaude } from '../claude'
import { canManageOrg } from '../permissions'
import BrandingSettings from './BrandingSettings'
import UserManagement from './UserManagement'
import LicenceSettings from './LicenceSettings'

const TABS = [
  { id: 'profile', label: 'Profile', icon: '🏢' },
  { id: 'licence', label: 'Licence', icon: '🔑' },
  { id: 'users', label: 'Users', icon: '👥' },
  { id: 'branding', label: 'Branding', icon: '🎨' },
  { id: 'ai', label: 'AI & Integrations', icon: '✦' },
]

const SECTORS = [
  'Central Government', 'Local Government', 'Defence', 'NHS & Health',
  'Education', 'Police & Emergency Services', 'Housing', 'Transport',
  'Utilities', 'Third Sector', 'Commercial'
]

const CAPABILITIES = [
  'Technology & Digital', 'Cyber Security', 'Managed Services',
  'Facilities Management', 'Professional Services', 'Consulting',
  'Engineering', 'Construction', 'Healthcare', 'Training & Education',
  'Data & Analytics', 'Cloud & Infrastructure', 'Software Development',
  'Programme Management', 'Finance & Accounting', 'Legal Services',
  'Marketing & Communications', 'HR & Workforce', 'Logistics & Supply Chain',
  'Environmental & Sustainability'
]

const CERTIFICATIONS = [
  'Cyber Essentials', 'Cyber Essentials Plus', 'ISO 27001', 'ISO 9001',
  'ISO 14001', 'SC Clearance', 'DV Clearance', 'G-Cloud', 'DOS',
  'SSAFA', 'Living Wage Employer', 'Disability Confident', 'JOSCAR'
]

function OrganisationSettings({ session, profile, onNavigate, orgId, orgName, onSettingsSaved, orgUsers, initialTab, onBack }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'profile')
  const [org, setOrg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)
  const [generatingProfile, setGeneratingProfile] = useState(false)

  const targetOrgId = orgId || profile?.organisation_id

  const [profileForm, setProfileForm] = useState({
    display_name: '',
    tagline: '',
    website_url: '',
    size: '',
    founded_year: '',
    hq_location: '',
    sectors: [],
    capabilities: [],
    certifications: [],
    geographic_reach: [],
    typical_value_min: '',
    typical_value_max: '',
    win_rate: '',
    strengths: '',
    weaknesses: '',
    strategic_priorities: '',
    key_clients: '',
    notable_wins: '',
  })

  const [aiForm, setAiForm] = useState({
    ai_enabled: false,
    sharepoint_url: '',
  })

  useEffect(() => { if (targetOrgId) loadOrg() }, [targetOrgId]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadOrg = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('organisations')
      .select('*')
      .eq('id', targetOrgId)
      .single()
    if (data) {
      setOrg(data)
      const p = data.org_profile || {}
      setProfileForm({
        display_name: p.display_name || data.name || '',
        tagline: p.tagline || '',
        website_url: p.website_url || '',
        size: p.size || '',
        founded_year: p.founded_year || '',
        hq_location: p.hq_location || '',
        sectors: p.sectors || [],
        capabilities: p.capabilities || [],
        certifications: p.certifications || [],
        geographic_reach: p.geographic_reach || [],
        typical_value_min: p.typical_value_min || '',
        typical_value_max: p.typical_value_max || '',
        win_rate: p.win_rate || '',
        strengths: p.strengths || '',
        weaknesses: p.weaknesses || '',
        strategic_priorities: p.strategic_priorities || '',
        key_clients: p.key_clients || '',
        notable_wins: p.notable_wins || '',
      })
      setAiForm({
        ai_enabled: data.ai_enabled || false,
        sharepoint_url: data.sharepoint_url || '',
      })
    }
    setLoading(false)
  }

  const handleSaveProfile = async () => {
    setSaving(true); setError(null)
    const { error } = await supabase
      .from('organisations')
      .update({ org_profile: profileForm })
      .eq('id', targetOrgId)
    if (error) { setError(error.message); setSaving(false); return }
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 3000)
    if (onSettingsSaved) onSettingsSaved()
  }

  const handleSaveAI = async () => {
    setSaving(true); setError(null)
    const { error } = await supabase
      .from('organisations')
      .update({ ai_enabled: aiForm.ai_enabled, sharepoint_url: aiForm.sharepoint_url || null })
      .eq('id', targetOrgId)
    if (error) { setError(error.message); setSaving(false); return }
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 3000)
    if (onSettingsSaved) onSettingsSaved()
  }

  const handleGenerateProfile = async () => {
    if (!profileForm.display_name) return
    setGeneratingProfile(true)
    try {
      const system = "You are an expert in UK Government and Defence procurement markets. Generate a structured organisation profile for a supplier. Be factual and commercially focused. Respond in JSON only."
      const prompt = "Generate a starter organisation profile for this company.\n\nCompany: " + profileForm.display_name + "\n" + (profileForm.website_url ? "Website: " + profileForm.website_url : "") + "\n" + (profileForm.size ? "Size: " + profileForm.size : "") + "\n\nRespond with JSON:\n{\n  \"tagline\": \"brief value proposition\",\n  \"strengths\": \"2-3 key competitive strengths\",\n  \"strategic_priorities\": \"likely strategic priorities\",\n  \"suggested_sectors\": [\"sector1\", \"sector2\"],\n  \"suggested_capabilities\": [\"cap1\", \"cap2\"],\n  \"typical_value_range\": \"typical contract value range\"\n}"
      const result = await callClaude({ system, prompt, max_tokens: 600 })
      const clean = result.replace(/```json/g, '').replace(/```/g, '').trim()
      const match = clean.match(/\{[\s\S]*\}/)
      if (match) {
        const parsed = JSON.parse(match[0])
        setProfileForm(prev => ({
          ...prev,
          tagline: parsed.tagline || prev.tagline,
          strengths: parsed.strengths || prev.strengths,
          strategic_priorities: parsed.strategic_priorities || prev.strategic_priorities,
          sectors: parsed.suggested_sectors?.filter(s => SECTORS.includes(s)) || prev.sectors,
          capabilities: parsed.suggested_capabilities?.filter(c => CAPABILITIES.includes(c)) || prev.capabilities,
        }))
      }
    } catch (err) { console.error('Profile gen error:', err) }
    setGeneratingProfile(false)
  }

  const toggleItem = (field, item) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }))
  }

  const chipStyle = (selected) => ({
    display: 'inline-block', padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600,
    border: '1px solid ' + (selected ? 'var(--green-dark)' : 'var(--silver)'),
    background: selected ? 'var(--green-dark)' : 'var(--white)',
    color: selected ? 'var(--white)' : 'var(--slate)',
    cursor: 'pointer', margin: '3px', borderRadius: '0',
    transition: 'all 0.15s'
  })

  if (!canManageOrg(profile)) return (
    <div style={{ padding: '48px', textAlign: 'center', color: 'var(--slate)' }}>
      You do not have permission to access Organisation Settings.
    </div>
  )

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      <div className="page-header">
        {onBack && <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--green)', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', padding: '0 0 8px 0', fontFamily: 'inherit' }}>← All Organisations</button>}
        <p className="label">Settings</p>
        <h1>{orgName || org?.name || 'Organisation Settings'}</h1>
        <p>Manage your organisation profile, users, branding and platform configuration.</p>
      </div>

      {error && <div className="error-msg">{error}</div>}
      {saved && <div style={{ background: 'var(--green-pale)', border: '1px solid var(--green)', padding: '12px 20px', marginBottom: '20px', fontSize: '0.875rem', color: 'var(--green-dark)', fontWeight: 600 }}>Settings saved successfully.</div>}

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--green-light)', marginBottom: '28px', gap: '4px' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: 'none', border: 'none', padding: '10px 20px',
              fontSize: '0.875rem', fontWeight: activeTab === tab.id ? 700 : 400,
              color: activeTab === tab.id ? 'var(--green-dark)' : 'var(--slate)',
              cursor: 'pointer', fontFamily: 'inherit',
              borderBottom: activeTab === tab.id ? '2px solid var(--green-dark)' : '2px solid transparent',
              marginBottom: '-2px'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: 'var(--slate)' }}>Loading...</p>}

      {!loading && activeTab === 'profile' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', color: 'var(--green-dark)', margin: 0 }}>Organisation Profile</h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--slate)', margin: '4px 0 0' }}>This profile is used by Cadence AI to tailor all intelligence outputs to your organisation. The more complete it is, the better the AI performs.</p>
            </div>
            {profile?.organisations?.ai_enabled && (
              <button
                onClick={handleGenerateProfile}
                disabled={generatingProfile || !profileForm.display_name}
                className="btn btn--secondary"
                style={{ flexShrink: 0, marginLeft: '16px' }}
              >
                {generatingProfile ? '⏳ Generating...' : '✦ AI Suggest'}
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>Display Name</label>
              <input type="text" value={profileForm.display_name} onChange={e => setProfileForm(p => ({ ...p, display_name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Tagline / Value Proposition</label>
              <input type="text" value={profileForm.tagline} onChange={e => setProfileForm(p => ({ ...p, tagline: e.target.value }))} placeholder="e.g. Technology-led services for Government and Defence" />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input type="text" value={profileForm.website_url} onChange={e => setProfileForm(p => ({ ...p, website_url: e.target.value }))} placeholder="https://..." />
            </div>
            <div className="form-group">
              <label>HQ Location</label>
              <input type="text" value={profileForm.hq_location} onChange={e => setProfileForm(p => ({ ...p, hq_location: e.target.value }))} placeholder="e.g. London, UK" />
            </div>
            <div className="form-group">
              <label>Organisation Size</label>
              <select value={profileForm.size} onChange={e => setProfileForm(p => ({ ...p, size: e.target.value }))}>
                <option value="">Select size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501-1000">501-1,000 employees</option>
                <option value="1001-5000">1,001-5,000 employees</option>
                <option value="5000+">5,000+ employees</option>
              </select>
            </div>
            <div className="form-group">
              <label>Founded Year</label>
              <input type="text" value={profileForm.founded_year} onChange={e => setProfileForm(p => ({ ...p, founded_year: e.target.value }))} placeholder="e.g. 2008" />
            </div>
            <div className="form-group">
              <label>Typical Contract Value Min (£)</label>
              <input type="number" value={profileForm.typical_value_min} onChange={e => setProfileForm(p => ({ ...p, typical_value_min: e.target.value }))} placeholder="e.g. 100000" />
            </div>
            <div className="form-group">
              <label>Typical Contract Value Max (£)</label>
              <input type="number" value={profileForm.typical_value_max} onChange={e => setProfileForm(p => ({ ...p, typical_value_max: e.target.value }))} placeholder="e.g. 10000000" />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '8px' }}>
            <label>Target Sectors</label>
            <div style={{ marginTop: '8px' }}>
              {SECTORS.map(s => (
                <span key={s} style={chipStyle(profileForm.sectors.includes(s))} onClick={() => toggleItem('sectors', s)}>{s}</span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Core Capabilities</label>
            <div style={{ marginTop: '8px' }}>
              {CAPABILITIES.map(c => (
                <span key={c} style={chipStyle(profileForm.capabilities.includes(c))} onClick={() => toggleItem('capabilities', c)}>{c}</span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Certifications & Accreditations</label>
            <div style={{ marginTop: '8px' }}>
              {CERTIFICATIONS.map(c => (
                <span key={c} style={chipStyle(profileForm.certifications.includes(c))} onClick={() => toggleItem('certifications', c)}>{c}</span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Competitive Strengths</label>
            <textarea
              value={profileForm.strengths}
              onChange={e => setProfileForm(p => ({ ...p, strengths: e.target.value }))}
              placeholder="What does your organisation genuinely do better than competitors? Be specific."
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label>Known Weaknesses / Gaps</label>
            <textarea
              value={profileForm.weaknesses}
              onChange={e => setProfileForm(p => ({ ...p, weaknesses: e.target.value }))}
              placeholder="Areas where you are less competitive. Used to calibrate AI qualification recommendations."
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label>Strategic Priorities (12-24 months)</label>
            <textarea
              value={profileForm.strategic_priorities}
              onChange={e => setProfileForm(p => ({ ...p, strategic_priorities: e.target.value }))}
              placeholder="What markets, sectors, or contract types are you prioritising? Used to weight AI qualification assessments."
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>Key Clients (optional)</label>
              <textarea
                value={profileForm.key_clients}
                onChange={e => setProfileForm(p => ({ ...p, key_clients: e.target.value }))}
                placeholder="e.g. MOD, HMRC, NHS England"
                rows={2}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
            <div className="form-group">
              <label>Notable Wins / Case Studies (optional)</label>
              <textarea
                value={profileForm.notable_wins}
                onChange={e => setProfileForm(p => ({ ...p, notable_wins: e.target.value }))}
                placeholder="Brief summary of relevant recent wins"
                rows={2}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="actions">
            <button className="btn btn--primary" onClick={handleSaveProfile} disabled={saving}>
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
            <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>Cancel</button>
          </div>
        </div>
      )}

      {!loading && activeTab === 'licence' && (
        <LicenceSettings session={session} profile={profile} onNavigate={onNavigate} targetOrgId={targetOrgId} />
      )}

      {!loading && activeTab === 'users' && (
        <UserManagement session={session} profile={profile} orgUsers={orgUsers} onNavigate={onNavigate} targetOrgId={targetOrgId} />
      )}

      {!loading && activeTab === 'branding' && (
        <BrandingSettings session={session} profile={profile} onNavigate={onNavigate} onSettingsSaved={onSettingsSaved} onBrandingUpdated={onSettingsSaved} targetOrgId={targetOrgId} />
      )}

      {!loading && activeTab === 'ai' && (
        <div>
          <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', color: 'var(--green-dark)', marginBottom: '8px' }}>AI & Integrations</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--slate)', marginBottom: '24px' }}>Configure AI features and external integrations for your organisation.</p>

          <div style={{ background: 'var(--white)', border: '1px solid var(--green-light)', marginBottom: '20px', borderLeft: '4px solid var(--green-dark)' }}>
            <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, paddingRight: '32px' }}>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.25rem', fontWeight: 600, color: 'var(--green-dark)', marginBottom: '8px' }}>AI Features</div>
                <p style={{ fontSize: '0.875rem', color: 'var(--charcoal)', margin: '0 0 8px', lineHeight: 1.6 }}>
                  Enable AI-powered features for your organisation. When active, relevant bid content is sent to the Anthropic API to generate insights.
                </p>
                <p style={{ fontSize: '0.8125rem', color: 'var(--slate)', margin: 0, lineHeight: 1.5 }}>
                  Includes CQ impact analysis, response question evidence retrieval, T&C variance analysis, competitor intelligence, opportunity pre-qualification, and qualification narrative generation. Disable if your organisation has restrictions on third-party AI processing.
                </p>
              </div>
              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div
                  onClick={() => setAiForm(prev => ({ ...prev, ai_enabled: !prev.ai_enabled }))}
                  style={{ width: '56px', height: '30px', borderRadius: '15px', background: aiForm.ai_enabled ? 'var(--green)' : 'var(--silver)', position: 'relative', cursor: 'pointer', transition: 'background 0.25s' }}
                >
                  <div style={{ position: 'absolute', top: '4px', left: aiForm.ai_enabled ? '30px' : '4px', width: '22px', height: '22px', borderRadius: '50%', background: 'var(--white)', transition: 'left 0.25s', boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }} />
                </div>
                <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: aiForm.ai_enabled ? 'var(--green)' : 'var(--slate)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  {aiForm.ai_enabled ? 'Enabled' : 'Disabled'}
                </div>
              </div>
            </div>
          </div>

          <div style={{ background: 'var(--white)', border: '1px solid var(--green-light)', marginBottom: '20px', borderLeft: '4px solid var(--green-mid)' }}>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.25rem', fontWeight: 600, color: 'var(--green-dark)', marginBottom: '8px' }}>Knowledge Library — SharePoint</div>
              <p style={{ fontSize: '0.875rem', color: 'var(--charcoal)', margin: '0 0 16px', lineHeight: 1.6 }}>
                Connect your SharePoint site to enable AI-powered evidence retrieval when working on response questions. Cadence searches your SharePoint and surfaces relevant case studies — no content is stored in Cadence.
              </p>
              <div className="form-group">
                <label>SharePoint Site URL</label>
                <input
                  type="text"
                  value={aiForm.sharepoint_url}
                  onChange={e => setAiForm(prev => ({ ...prev, sharepoint_url: e.target.value }))}
                  placeholder="https://yourcompany.sharepoint.com/sites/bids"
                />
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--slate)', margin: '8px 0 0', fontStyle: 'italic' }}>
                Full Microsoft Graph API authentication coming in Phase 2. Save your URL now to configure ahead of that release.
              </p>
            </div>
          </div>

          <div className="actions">
            <button className="btn btn--primary" onClick={handleSaveAI} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrganisationSettings
